import { ApiImageObject, ImageSource, ImageSourceKey } from './types';

const IMAGE_SOURCE_WIDTHS: Record<ImageSourceKey, number> = {
  thumbnail: 120,
  small: 320,
  medium: 768,
  large: 1280,
  xlarge: 1920,
  file: 2560,
};

const FALLBACK_ORDER: ImageSourceKey[] = [
  'thumbnail',
  'small',
  'medium',
  'large',
  'xlarge',
  'file',
];

const srcSetCache = new Map<string, string | null>();

const hasValue = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export const isApiImageObject = (image: unknown): image is ApiImageObject =>
  !!image &&
  typeof image === 'object' &&
  FALLBACK_ORDER.some(key => hasValue((image as ApiImageObject)[key]));

export const getRawImageSrc = (image: ImageSource): string | null => {
  if (hasValue(image)) {
    return image;
  }

  if (isApiImageObject(image)) {
    return null;
  }

  if (hasValue((image as unknown as ApiImageObject)?.src)) {
    return (image as unknown as ApiImageObject).src!;
  }

  return null;
};

const buildCacheKey = (image: ApiImageObject): string =>
  FALLBACK_ORDER.map(key => `${key}:${image?.[key] || ''}`).join('|');

export const getFallbackImageSrc = (image: ImageSource, preferredKey?: ImageSourceKey): string | null => {
  const rawSrc = getRawImageSrc(image);

  if (rawSrc) {
    return rawSrc;
  }

  if (!isApiImageObject(image)) {
    return null;
  }

  if (preferredKey && hasValue(image?.[preferredKey])) {
    return image[preferredKey]!;
  }

  const fallbackKey = FALLBACK_ORDER.find(key => hasValue(image?.[key]));
  return fallbackKey ? image[fallbackKey]! : null;
};

export const buildImageSrcSet = (image: ImageSource): string | null => {
  if (!isApiImageObject(image)) {
    return null;
  }

  const cacheKey = buildCacheKey(image);
  if (srcSetCache.has(cacheKey)) {
    return srcSetCache.get(cacheKey)!;
  }

  const usedSources = new Set<string>();
  const srcSet = FALLBACK_ORDER.filter(key => hasValue(image?.[key]))
    .filter(key => {
      if (usedSources.has(image[key]!)) {
        return false;
      }
      usedSources.add(image[key]!);
      return true;
    })
    .map(key => `${image[key]} ${IMAGE_SOURCE_WIDTHS[key]}w`)
    .join(', ');

  const value = srcSet || null;
  srcSetCache.set(cacheKey, value);
  return value;
};